import { useEffect, useState, type ReactNode } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fetchSalary, type SalaryResponse } from '../api';
import type { Language } from '@/types';

interface SalaryWorkspaceProps {
  language: Language;
}

const copy = {
  en: {
    title: 'Payroll',
    adminOnly: 'Only admins and supervisors can view payroll.',
    prev: 'Previous',
    next: 'Next',
    refresh: 'Refresh',
    colCleaner: 'Cleaner',
    colPoints: 'Points',
    colSalary: 'Salary',
    colStatus: 'Status',
    rateMissing: 'Rate missing',
    pending: 'unpaid',
    paid: 'paid',
    kpiCleaners: 'Cleaners',
    kpiPoints: 'Points',
    kpiTotal: 'Total salary',
    kpiPending: 'Unpaid',
    kpiPaid: 'Paid',
    emptyState: (month: string) => `No approved points recorded for ${month} yet.`,
    footer: 'Total',
  },
  sv: {
    title: 'Löneunderlag',
    adminOnly: 'Endast admin och städledare kan se löneunderlag.',
    prev: 'Föregående',
    next: 'Nästa',
    refresh: 'Uppdatera',
    colCleaner: 'Städare',
    colPoints: 'Poäng',
    colSalary: 'Lön',
    colStatus: 'Status',
    rateMissing: 'Taxa saknas',
    pending: 'ej utbetald',
    paid: 'utbetald',
    kpiCleaners: 'Städare',
    kpiPoints: 'Poäng',
    kpiTotal: 'Total lön',
    kpiPending: 'Ej utbetald',
    kpiPaid: 'Utbetald',
    emptyState: (month: string) => `Inga godkända poäng registrerade för ${month} ännu.`,
    footer: 'Totalt',
  },
} as const;

const MONTH_NAMES_SV = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December',
];
const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shift(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function toDateRange(year: number, month: number): { from: string; to: string } {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

function formatKr(value: number): string {
  return (
    new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(value) + ' kr'
  );
}

export function SalaryWorkspace({ language }: SalaryWorkspaceProps) {
  const { session, profile } = useAuth();
  const text = copy[language];
  const token = session?.access_token ?? null;
  const canView = profile?.role === 'admin' || profile?.role === 'supervisor';

  const { year: initY, month: initM } = currentYearMonth();
  const [year, setYear] = useState(initY);
  const [month, setMonth] = useState(initM);
  const [data, setData] = useState<SalaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const monthNames = language === 'sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN;
  const monthLabel = monthNames[month - 1];

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { from, to } = toDateRange(year, month);
      const res = await fetchSalary(from, to, token);
      setData(res);
    } catch (err) {
      console.error('Failed to load salary data', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, month, canView]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="cmd-panel cmd-panel-inner rounded p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-[#607C6E] mx-auto mb-3" />
          <p className="text-sm font-mono text-[#2E4640]">{text.adminOnly}</p>
        </div>
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-lg font-mono font-semibold tracking-tight text-[#073331]">{text.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center cmd-panel rounded">
            <button
              type="button"
              className="h-9 w-9 flex items-center justify-center text-[#607C6E] hover:text-[#073331] transition-colors"
              onClick={() => {
                const n = shift(year, month, -1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label={text.prev}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[150px] text-center text-sm font-mono font-semibold text-[#073331] px-2">
              {monthLabel} {year}
            </div>
            <button
              type="button"
              className="h-9 w-9 flex items-center justify-center text-[#607C6E] hover:text-[#073331] transition-colors"
              onClick={() => {
                const n = shift(year, month, 1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label={text.next}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1.5">{text.refresh}</span>
          </Button>
        </div>
      </div>

      {/* KPI tiles — white stat tiles */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile label={text.kpiCleaners} value={String(totals.cleaners)} />
          <StatTile label={text.kpiPoints} value={String(totals.points)} />
          <StatTile label={text.kpiTotal} value={formatKr(totals.kr)} tone="strong" />
          <StatTile label={text.kpiPending} value={formatKr(totals.pending_kr)} tone="warn" />
          <StatTile label={text.kpiPaid} value={formatKr(totals.paid_kr)} tone="ok" />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#607C6E]" />
        </div>
      ) : rows.length === 0 ? (
        <div className="cmd-panel cmd-panel-inner rounded py-10 text-center text-sm font-mono text-[#607C6E]">
          {text.emptyState(`${monthLabel} ${year}`)}
        </div>
      ) : (
        <div className="cmd-panel cmd-panel-inner rounded overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left font-normal px-4 py-2 border-b border-[#073331]/10">{text.colCleaner}</th>
                <th className="cmd-label text-right font-normal px-4 py-2 border-b border-[#073331]/10 w-24">{text.colPoints}</th>
                <th className="cmd-label text-right font-normal px-4 py-2 border-b border-[#073331]/10 w-40">{text.colSalary}</th>
                <th className="cmd-label text-left font-normal px-4 py-2 border-b border-[#073331]/10">{text.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = row.cleaner_name ?? 'Okänd';
                const missingRate = row.kr_per_point === null;

                return (
                  <tr key={row.user_id} className="border-b border-[#073331]/[0.06] last:border-b-0 hover:bg-[#073331]/[0.04] transition-colors group">
                    <td className="px-4 py-1.5 text-sm font-mono text-[#2E4640] group-hover:text-[#073331] transition-colors truncate">{name}</td>
                    <td className="px-4 py-1.5 text-right text-sm font-mono tabular-nums text-[#607C6E]">
                      {row.points}
                    </td>
                    <td className={cn(
                      'px-4 py-1.5 text-right text-sm font-mono tabular-nums',
                      missingRate ? 'text-[#607C6E] italic' : 'text-[#073331]',
                    )}>
                      {missingRate ? text.rateMissing : formatKr(row.kr)}
                    </td>
                    <td className="px-4 py-1.5">
                      <div className="flex flex-wrap gap-1.5">
                        {row.paid_kr > 0 && <StatusChip tone="ok">{formatKr(row.paid_kr)} {text.paid}</StatusChip>}
                        {row.pending_kr > 0 && <StatusChip tone="warn">{formatKr(row.pending_kr)} {text.pending}</StatusChip>}
                        {row.paid_kr === 0 && row.pending_kr === 0 && !missingRate && (
                          <span className="text-xs font-mono text-[#607C6E]">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="border-t border-[#073331]/15 bg-[#073331]/[0.03]">
                  <td className="px-4 py-1.5 cmd-label text-[#073331]">{text.footer}</td>
                  <td className="px-4 py-1.5 text-right text-sm font-mono font-semibold tabular-nums text-[#073331]">{totals.points}</td>
                  <td className="px-4 py-1.5 text-right text-sm font-mono font-semibold tabular-nums text-[#073331]">{formatKr(totals.kr)}</td>
                  <td className="px-4 py-1.5">
                    <div className="flex flex-wrap gap-1.5">
                      {totals.paid_kr > 0 && <StatusChip tone="ok">{formatKr(totals.paid_kr)} {text.paid}</StatusChip>}
                      {totals.pending_kr > 0 && <StatusChip tone="warn">{formatKr(totals.pending_kr)} {text.pending}</StatusChip>}
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

const TONE_NUMERAL: Record<string, string> = {
  ok: '#1B7F4D',
  warn: '#B97E1F',
  strong: '#073331',
  neutral: '#073331',
};

interface StatTileProps {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'strong' | 'neutral';
}

function StatTile({ label, value, tone = 'neutral' }: StatTileProps) {
  return (
    <div className="cmd-panel cmd-panel-inner rounded px-3 py-2 flex flex-col gap-1">
      <span className="cmd-label truncate">{label}</span>
      <span
        className="text-[22px] leading-none font-mono tabular-nums font-semibold mt-0.5"
        style={{ color: TONE_NUMERAL[tone] }}
      >
        {value}
      </span>
    </div>
  );
}

const CHIP_STYLE: Record<string, { bg: string; fg: string }> = {
  ok: { bg: 'rgba(27,127,77,0.10)', fg: '#1B7F4D' },
  warn: { bg: 'rgba(185,126,31,0.12)', fg: '#B97E1F' },
};

function StatusChip({ tone, children }: { tone: 'ok' | 'warn'; children: ReactNode }) {
  const s = CHIP_STYLE[tone];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono tabular-nums"
      style={{ background: s.bg, color: s.fg }}
    >
      {children}
    </span>
  );
}
